
import firebase from 'firebase/compat/app'
import 'firebase/compat/auth'
import { httpsCallable } from 'firebase/functions'
import { functions } from '@/firebase/functions'
import Mixins from '@/shared'
import { showSnackbar } from '@/globalActions'
import StandingsTable from '@/components/StandingsTable.component.vue'

export default {
  name: 'GroupStandings',
  components: {
    StandingsTable
  },
  mixins: [Mixins],
  props: {
    groupId: {
      type: String,
      required: true,
      default: 'null'
    }
  },
  data: () => ({
    loading: false,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    standingsTable: [] as any[],
    currentTableId: null as string | null
  }),
  watch: {
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    groupId () {
      this.loadStandings()
    }
  },
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  created () {
    this.loadStandings()
  },
  methods: {
    /**
     * Same data as `webApi` GET `/groups/:groupId/standings`: latest `standings` doc by `created_at`.
     */
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    async loadStandings () {
      if (this.groupId === 'null' || this.groupId === undefined) {
        this.loading = false
        return
      }
      this.loading = true
      try {
        const snap = await firebase.firestore()
          .collection('standings')
          .where('groupId', '==', this.groupId)
          .get()
        if (snap.empty) {
          this.standingsTable = []
          this.currentTableId = null
          return
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const rows: any[] = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        // Firestore field names use snake_case (matches `sendGroupStandings` in functions).
        rows.sort((a, b) => {
          const ta = a.created_at && a.created_at.toMillis ? a.created_at.toMillis() : 0
          const tb = b.created_at && b.created_at.toMillis ? b.created_at.toMillis() : 0
          return tb - ta
        })
        const latest = rows[0]
        this.currentTableId = latest ? latest.id : null
        const table = latest ? latest.table : undefined
        this.standingsTable = []
        if (Array.isArray(table)) {
          // Table array order is defined when the snapshot is generated (functions).
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          this.standingsTable = (table as any[]).slice()
        } else {
          await this.fillEmptyTable()
        }
      } catch {
        await this.fillEmptyTable()
      } finally {
        this.loading = false
      }
    },
    /**
     * Same callable pattern as MatchReport (`firebase.functions().httpsCallable(...)`).
     */
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    async generateStandingSnapshot () {
      if (!firebase.auth().currentUser) {
        return
      }
      this.loading = true
      try {
        const generate = httpsCallable(functions, 'generateStandingSnapshotV2')
        await generate({ groupId: this.groupId })
        showSnackbar(this.$t('standings-snapshot-submitted') as string)
        await this.loadStandings()
      } catch (err) {
        console.error('generateStandingSnapshotV2', err)
        showSnackbar(this.$t('standings-snapshot-failed') as string)
      } finally {
        this.loading = false
      }
    },
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    async fillEmptyTable () {
      this.standingsTable = []
      if (this.groupId !== undefined) {
        await this.$store.dispatch('GroupModule/fetchById', this.groupId).then((data: { teams: unknown[] }) => {
          if ('teams' in data) {
            data.teams.forEach(team => {
              this.standingsTable.push(team)
            })
            this.loading = false
            return
          }
          this.loading = !this.loading
        })
      } else {
        this.loading = false
      }
    }
  }
}
